import { z } from 'zod';

/**
 * Where a recorded match stands with a human (`03-trust-ban-evasion.md`
 * §false-positive path).
 *
 * Every match — flagged **or** blocked — is visible and reversible in the Trust
 * queue. That is the whole answer to "what happens when the pipeline is wrong",
 * and it is why `block` is allowed to be a default at all: a refusal is recorded
 * rather than silent, so staff can find it.
 */
export const SIGNAL_MATCH_STATUSES = ['pending_review', 'confirmed', 'dismissed'] as const;

export const SignalMatchStatusSchema = z.enum(SIGNAL_MATCH_STATUSES);

export type SignalMatchStatus = (typeof SIGNAL_MATCH_STATUSES)[number];

/**
 * The verdicts a reviewer may write.
 *
 * A **narrowing** of {@link SIGNAL_MATCH_STATUSES}, not a second list: a review
 * request that could name `pending_review` would be a request to un-decide,
 * which no surface offers and which would silently un-ban a confirmed account.
 * `confirmed` bans the subject through `UserStatusService` (N9); `dismissed`
 * whitelists the signal **pair**, so the same two signals never re-flag and the
 * queue actually empties.
 */
export const SIGNAL_MATCH_RESOLUTIONS = ['confirmed', 'dismissed'] as const;

export const SignalMatchResolutionSchema = z.enum(SIGNAL_MATCH_RESOLUTIONS);

export type SignalMatchResolution = (typeof SIGNAL_MATCH_RESOLUTIONS)[number];

const RESOLVED: ReadonlySet<string> = new Set<string>(SIGNAL_MATCH_RESOLUTIONS);

/** Whether a human has already ruled — i.e. whether the row has left the queue. */
export function isResolvedMatchStatus(status: SignalMatchStatus): boolean {
  return RESOLVED.has(status);
}

export function isSignalMatchResolution(value: string): value is SignalMatchResolution {
  return RESOLVED.has(value);
}
